import Breadcrumb from "components/Common/Breadcrumb"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { FaFilter } from "react-icons/fa"
import Select from "react-select"
import { Button, Card, CardBody, Col, Row } from "reactstrap"
import { customStyles } from "utils/SelectStyles"
import AreaLineChart from "./AreaLineChart"
import PieChart from "./PieChart"
import HorizontalBarChart from "./HorizontalBarChart"
import StackedBarChart from "./StackedBarChart"
import { fetchData, FetchPostData } from "utils/Common_api_functions"
import Loader from "components/Loader/loader"
import { DatePicker } from "antd"
import { months } from "utils/defaultpositions"
import secureLocalStorage from "react-secure-storage"
import Gauge from "pages/Dashboard/GuageChart"
import Swal from "sweetalert2"
import html2canvas from "html2canvas"
import jsPDF from "jspdf"
import { MdDownload } from "react-icons/md"
import dayjs from "dayjs"
import moment from "moment"
import CombinedContributionBar from "./CombinedContributionBar"
import CategoryContributionChart from "./CategoryContributionChart"
import ProductContributionChart from "./ProductContributionChart"
import CustomerContributionChart from "./CustomerContributionChart"
import GrossMarginProductChart from "./GrossMarginProductChart"
import GrossMarginCustomerChart from "./GrossMarginCustomerChart"

const { RangePicker } = DatePicker

// ── Subcategory Names Map ──────────────────────────────────────────────────
const SUBCATEGORY_NAMES = {
  // Category 1 - Sausages
  "1": "Sausages",
  "1.1": "Traditional Sausages",
  "1.2": "Chipolatas",
  "1.3": "Catering Sausages",
  "1.4": "Beef Sausages",
  "1.5": "Cocktail Sausages",
  "1.6": "Flavoured Sausages",
  "1.7": "Hafners",
  "1.8": "Jumbo Sausages",
  "1.9": "Speciality Sausages",
  "1.31": "Catering Sausages",
  "1.32": "Catering Sausages",

  // Category 2 - Fresh Pork
  "2": "Fresh Pork",
  "2.1": "Fresh Pork Cuts",
  "2.2": "Fresh Pork Primal",
  "2.3": "Belly Pork",
  "2.4": "Pork Ribs",

  // Category 3 - Puddings
  "3": "Puddings",
  "3.1": "Black Pudding",
  "3.2": "White Pudding",
  "3.3": "Vegetable Pudding",

  // Category 4 - Bacon & Ham
  "4": "Bacon & Ham",
  "4.1": "Bacon",
  "4.2": "Smoked Bacon",
  "4.3": "Bacon",
  "4.4": "Gammon",
  "4.5": "Ham",
  "4.6": "Cooked Meats",

  // Category 5 - Cured
  "5": "Cured",
  "5.1": "Cured Pork",
  "5.2": "Cured Products",
}

const getSubCategoryName = r => {
  if (
    r.subCategoryName &&
    isNaN(r.subCategoryName) &&
    !/^\d+(\.\d+)?$/.test(String(r.subCategoryName).trim())
  ) {
    return r.subCategoryName
  }
  const rawCode = String(r.subCategory || r.subCategoryTwo || r.category || "").trim()
  if (SUBCATEGORY_NAMES[rawCode]) return SUBCATEGORY_NAMES[rawCode]

  const sc = String(r.subCategory || "").trim()
  if (SUBCATEGORY_NAMES[sc]) return SUBCATEGORY_NAMES[sc]

  const s2 = String(r.subCategoryTwo || "").trim()
  if (SUBCATEGORY_NAMES[s2]) return SUBCATEGORY_NAMES[s2]

  if (sc.startsWith("1.7")) return "Hafners"
  if (sc.startsWith("1.3")) return "Catering Sausages"
  if (sc.startsWith("1.2")) return "Chipolatas"
  if (sc.startsWith("1.1")) return "Traditional Sausages"
  if (sc.startsWith("4.3")) return "Bacon"
  if (sc.startsWith("4.5")) return "Ham"
  if (sc.startsWith("5.1")) return "Cured"
  if (sc.startsWith("3")) return "Puddings"
  if (sc.startsWith("2")) return "Fresh Pork"
  if (sc.startsWith("1")) return "Sausages"

  return rawCode || "Other"
}

const currentYear = new Date().getFullYear()
const years = Array.from({ length: currentYear - 2020 }, (_, i) => ({
  label: `${2021 + i}`,
  value: 2021 + i,
}))?.reverse()

const PLIndex = () => {
  const [loading, setLoading] = useState(false)
  const [PLData, setPLData] = useState({})
  const [customerReportData, setCustomerReportData] = useState({})
  const [dateRange, setDateRange] = useState([])
  const [selectedMonths, setSelectedMonths] = useState([])
  const [selectedYears, setSelectedYears] = useState([])

  const [sortGrossPound, setSortGrossPound] = useState("chronological")
  const [sortGrossPct, setSortGrossPct] = useState("chronological")
  const [sortNetPound, setSortNetPound] = useState("chronological")
  const [sortNetPct, setSortNetPct] = useState("chronological")
  const [ranges, setRanges] = useState([])
  const [netPL, setNetPL] = useState("0")
  const [currentweek, setCurrentWeek] = useState([])
  const [maxDate, setMaxDate] = useState(null)

  useEffect(() => {
    const Years = secureLocalStorage?.getItem("Years")
    const Months = secureLocalStorage?.getItem("Months")
    const DateRange = secureLocalStorage?.getItem("DateRange")
    const FinalRangeRaw = secureLocalStorage?.getItem("FINALRANGE")

    const initialYears =
      Years && Array.isArray(Years) && Years.length > 0
        ? Years
        : [{ label: `${currentYear}`, value: currentYear }]

    setSelectedYears(initialYears)
    setSelectedMonths(Months || [])

    // Default filter should not restrict to a single week
    setDateRange([])
    setMaxDate(FinalRangeRaw?.[1] ? dayjs(FinalRangeRaw[1]) : null)

    const activeDateRaw = DateRange ?? FinalRangeRaw
    setCurrentWeek(activeDateRaw ? activeDateRaw.map(d => dayjs(d)) : [])

    const fetchInitialData = async () => {
      const payload = {
        startDate: undefined,
        endDate: undefined,
        month: Months?.map(i => i?.value)?.join(",") || undefined,
        year: initialYears?.map(i => i?.value)?.join(",") || `${currentYear}`,
      }

      const APidata = await FetchPostData(
        setLoading,
        "sheet/profit/and/loss",
        payload
      )

      if (APidata?.data?.success) {
        setPLData(APidata?.data)
        const NetPL = APidata?.data?.plData?.length
          ? APidata?.data?.plData.reduce(
            (sum, item) => sum + Number(item.sumGrossMarginValue),
            0
          ) / APidata?.data?.plData.length
          : 0
        setNetPL(NetPL.toFixed(0))
      } else {
        setPLData({})
        console.log(APidata?.response)
      }

      const reportRes = await FetchPostData(
        setLoading,
        "sheet/customer/report",
        payload
      )
      if (reportRes?.data?.success) {
        setCustomerReportData(reportRes.data?.data)
      } else {
        setCustomerReportData({})
      }
    }

    fetchInitialData()
    fetchRanges()
  }, [])

  async function fetchRanges() {
    const APIdata = await fetchData(setLoading, "gauge/ranges")
    if (APIdata?.success) {
      setRanges(APIdata?.data)
    } else {
      Swal.fire({
        title: "Error Fetching Ranges",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const handleFilter = async () => {
    const filterPayload = {
      startDate: dateRange?.[0]?.isValid()
        ? dateRange[0].format("YYYY-MM-DD")
        : undefined,
      endDate: dateRange?.[1]?.isValid()
        ? dateRange[1].format("YYYY-MM-DD")
        : undefined,
      month: selectedMonths?.map(item => item?.value)?.join(","),
      year: selectedYears?.map(item => item?.value)?.join(","),
    }

    const APidata = await FetchPostData(
      setLoading,
      "sheet/profit/and/loss",
      filterPayload
    )

    if (APidata?.data?.success) {
      setPLData(APidata?.data)
      const NetPL = APidata?.data?.plData?.length
        ? APidata?.data?.plData.reduce(
          (sum, item) => sum + Number(item.sumGrossMarginValue),
          0
        ) / APidata?.data?.plData.length
        : 0
      setNetPL(NetPL.toFixed(0))
    } else {
      setPLData({})
      console.log(APidata?.response)
    }

    const reportRes = await FetchPostData(
      setLoading,
      "sheet/customer/report",
      filterPayload
    )
    if (reportRes?.data?.success) {
      setCustomerReportData(reportRes.data?.data)
    } else {
      setCustomerReportData({})
    }
  }

  const chartData = useMemo(() => {
    const data = PLData?.plData || []
    const data2 = PLData?.secondYearPLData || []
    const finalData = PLData?.netMarginFinalData || []

    const buildWeekAligned = (arr1, key1, arr2, key2, multiply = false) => {
      const weekSet = new Set()
      arr1.forEach(item => weekSet.add(moment(item.date).isoWeek()))
      arr2.forEach(item => weekSet.add(moment(item.date).isoWeek()))
      const allWeeks = Array.from(weekSet).sort((a, b) => a - b)

      const map1 = {}
      arr1.forEach(item => {
        const val = multiply ? Number(item[key1]) * 100 : Number(item[key1])
        map1[moment(item.date).isoWeek()] = isNaN(val)
          ? null
          : Number(val.toFixed(0))
      })

      const map2 = {}
      arr2.forEach(item => {
        const val = multiply ? Number(item[key2]) * 100 : Number(item[key2])
        map2[moment(item.date).isoWeek()] = isNaN(val)
          ? null
          : Number(val.toFixed(0))
      })

      const dateByWeek = {}
      arr1.forEach(item => {
        dateByWeek[moment(item.date).isoWeek()] = moment(item.date).format(
          "DD/MM/YYYY"
        )
      })

      const prevDateByWeek = {}
      arr2.forEach(item => {
        prevDateByWeek[moment(item.date).isoWeek()] = moment(item.date).format(
          "DD/MM/YYYY"
        )
      })

      return {
        allWeeks,
        data: allWeeks.map(w => map1[w] ?? null),
        data2: allWeeks.map(w => map2[w] ?? null),
        dateByWeek,
        prevDateByWeek,
      }
    }

    return {
      netMargin: buildWeekAligned(
        data,
        "sumNetMarginContribution",
        data2,
        "sumNetMargin"
      ),
      netMarginPercent: buildWeekAligned(
        data,
        "avgNetMarginPercent",
        data2,
        "avgNetMarginPercent",
        true
      ),
      grossMargin: buildWeekAligned(
        data,
        "sumGrossMarginValue",
        data2,
        "sumGrossMargin"
      ),
      grossMarginPercent: buildWeekAligned(
        data,
        "avgGrossMarginPercent",
        data2,
        "avgGrossMarginPercent",
        true
      ),
      netMarginFinal: buildWeekAligned(
        finalData,
        "netMarginFinal",
        [],
        "netMarginFinal"
      ),
      netMarginFinalPercent: buildWeekAligned(
        finalData,
        "netMarginPercentage",
        [],
        "netMarginPercentage",
        true
      ),
    }
  }, [PLData])

  const customerContribution = useMemo(() => {
    const data = PLData?.customerContribution || []
    return {
      labels: data.map(item => item.customerName),
      series: data.map(item =>
        Math.abs(Number(item.totalNetMarginContribution))
      ),
    }
  }, [PLData])

  const reportRawRecords = useMemo(() => {
    if (!customerReportData || typeof customerReportData !== "object")
      return []
    return Array.isArray(customerReportData)
      ? customerReportData
      : Object.values(customerReportData)
  }, [customerReportData])

  // Grouped by product → { productName, totalNetMarginContribution, data: [{ customerName, categoryName, netMarginPercent, netMarginContribution }] }
  const productContributionGrouped = useMemo(() => {
    const map = {}
    reportRawRecords.forEach(r => {
      const pc = r.productCode || r.productName
      if (!pc) return
      if (!map[pc]) {
        map[pc] = { productName: r.productName, customers: {} }
      }
      const cc = r.customerCode || r.customerName
      if (!map[pc].customers[cc]) {
        map[pc].customers[cc] = {
          customerName: r.customerName || r.customerCode || "Unknown",
          categoryName: getSubCategoryName(r),
          salesValue: 0,
          netMarginContribution: 0,
        }
      }
      map[pc].customers[cc].salesValue += Number(
        r.weeklySalesValue || r.salesValue || 0
      )
      map[pc].customers[cc].netMarginContribution += Number(
        r.netMarginContribution || 0
      )
    })

    return Object.values(map).map(prod => {
      const data = Object.values(prod.customers).map(c => ({
        customerName: c.customerName,
        categoryName: c.categoryName,
        netMarginContribution: c.netMarginContribution,
        netMarginPercent:
          c.salesValue !== 0
            ? (c.netMarginContribution / c.salesValue) * 100
            : 0,
      }))
      return {
        productName: prod.productName,
        totalNetMarginContribution: data.reduce(
          (s, c) => s + Number(c.netMarginContribution),
          0
        ),
        data,
      }
    })
  }, [reportRawRecords])

  // Grouped by customer → { customerName, totalNetMarginContribution, data: [{ productName, categoryName, netMarginPercent, netMarginContribution }] }
  const customerContributionGrouped = useMemo(() => {
    const map = {}
    reportRawRecords.forEach(r => {
      const cc = r.customerCode || r.customerName
      if (!cc) return
      if (!map[cc]) {
        map[cc] = { customerName: r.customerName, products: {} }
      }
      const pc = r.productCode || r.productName
      if (!map[cc].products[pc]) {
        map[cc].products[pc] = {
          productName: r.productName || r.productCode || "Unknown",
          categoryName: getSubCategoryName(r),
          salesValue: 0,
          netMarginContribution: 0,
        }
      }
      map[cc].products[pc].salesValue += Number(
        r.weeklySalesValue || r.salesValue || 0
      )
      map[cc].products[pc].netMarginContribution += Number(
        r.netMarginContribution || 0
      )
    })

    return Object.values(map).map(cust => {
      const data = Object.values(cust.products).map(p => ({
        productName: p.productName,
        categoryName: p.categoryName,
        netMarginContribution: p.netMarginContribution,
        netMarginPercent:
          p.salesValue !== 0
            ? (p.netMarginContribution / p.salesValue) * 100
            : 0,
      }))
      return {
        customerName: cust.customerName,
        totalNetMarginContribution: data.reduce(
          (s, p) => s + Number(p.netMarginContribution),
          0
        ),
        data,
      }
    })
  }, [reportRawRecords])

  // Grouped by product → { productName, totalGrossMarginContribution, data: [{ customerName, categoryName, grossMarginPercent, grossMarginContribution }] }
  const grossMarginProductGrouped = useMemo(() => {
    const map = {}
    reportRawRecords.forEach(r => {
      const pc = r.productCode || r.productName
      if (!pc) return
      if (!map[pc]) {
        map[pc] = { productName: r.productName, customers: {} }
      }
      const cc = r.customerCode || r.customerName
      if (!map[pc].customers[cc]) {
        map[pc].customers[cc] = {
          customerName: r.customerName || r.customerCode || "Unknown",
          categoryName: getSubCategoryName(r),
          salesValue: 0,
          grossMarginContribution: 0,
        }
      }
      map[pc].customers[cc].salesValue += Number(
        r.weeklySalesValue || r.salesValue || 0
      )
      map[pc].customers[cc].grossMarginContribution += Number(
        r.grossMarginValue || 0
      )
    })

    return Object.values(map).map(prod => {
      const data = Object.values(prod.customers).map(c => ({
        customerName: c.customerName,
        categoryName: c.categoryName,
        grossMarginContribution: c.grossMarginContribution,
        grossMarginPercent:
          c.salesValue !== 0
            ? (c.grossMarginContribution / c.salesValue) * 100
            : 0,
      }))
      return {
        productName: prod.productName,
        totalGrossMarginContribution: data.reduce(
          (s, c) => s + Number(c.grossMarginContribution),
          0
        ),
        data,
      }
    })
  }, [reportRawRecords])

  // Grouped by customer → { customerName, totalGrossMarginContribution, data: [{ productName, categoryName, grossMarginPercent, grossMarginContribution }] }
  const grossMarginCustomerGrouped = useMemo(() => {
    const map = {}
    reportRawRecords.forEach(r => {
      const cc = r.customerCode || r.customerName
      if (!cc) return
      if (!map[cc]) {
        map[cc] = { customerName: r.customerName, products: {} }
      }
      const pc = r.productCode || r.productName
      if (!map[cc].products[pc]) {
        map[cc].products[pc] = {
          productName: r.productName || r.productCode || "Unknown",
          categoryName: getSubCategoryName(r),
          salesValue: 0,
          grossMarginContribution: 0,
        }
      }
      map[cc].products[pc].salesValue += Number(
        r.weeklySalesValue || r.salesValue || 0
      )
      map[cc].products[pc].grossMarginContribution += Number(
        r.grossMarginValue || 0
      )
    })

    return Object.values(map).map(cust => {
      const data = Object.values(cust.products).map(p => ({
        productName: p.productName,
        categoryName: p.categoryName,
        grossMarginContribution: p.grossMarginContribution,
        grossMarginPercent:
          p.salesValue !== 0
            ? (p.grossMarginContribution / p.salesValue) * 100
            : 0,
      }))
      return {
        customerName: cust.customerName,
        totalGrossMarginContribution: data.reduce(
          (s, p) => s + Number(p.grossMarginContribution),
          0
        ),
        data,
      }
    })
  }, [reportRawRecords])

  const negativeCustomerChart = useMemo(() => {
    const data = PLData?.negetiveCustomerContribution || []
    return {
      categories: data.map(item => item.customerName),
      data: data.map(item => Number(item.totalNetMarginContribution)),
    }
  }, [PLData])

  const positiveCustomerChart = useMemo(() => {
    const data = PLData?.positiveCustomerContribution || []
    return {
      categories: data.map(item => item.customerName),
      data: data.map(item => Number(item.totalNetMarginContribution)),
    }
  }, [PLData])

  const dashboardRef = useRef(null)

  const handleExport = async () => {
    const element = dashboardRef.current
    if (!element) return

    try {
      setLoading(true)

      const originalHeight = element.style.height
      const originalOverflow = element.style.overflow
      element.style.height = "auto"
      element.style.overflow = "visible"

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop

      const canvas = await html2canvas(element, {
        scale: 1.2,
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: -scrollTop,
        y: 0,
        x: 0,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        logging: false,
      })

      element.style.height = originalHeight
      element.style.overflow = originalOverflow

      const imgData = canvas.toDataURL("image/jpeg", 0.75)
      const imgWidth = canvas.width / 1.2
      const imgHeight = canvas.height / 1.2
      const marginLeft = 30
      const marginRight = 30
      const marginTop = 10
      const marginBottom = 20
      const pdfWidth = imgWidth + marginLeft + marginRight
      const pdfHeight = imgHeight + marginTop + marginBottom

      const pdf = new jsPDF({
        orientation: pdfWidth > pdfHeight ? "landscape" : "portrait",
        unit: "px",
        format: [pdfWidth, pdfHeight],
        compress: true,
      })

      pdf.addImage(
        imgData,
        "JPEG",
        marginLeft,
        marginTop,
        imgWidth,
        imgHeight,
        undefined,
        "FAST"
      )

      pdf.save(`PL_Charts_${dayjs().format("DD-MM-YYYY")}.pdf`)
    } catch (e) {
      Swal.fire({
        title: "Export Failed",
        text: "Something went wrong while exporting.",
        icon: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="page-content" ref={dashboardRef}>
      {loading && <Loader />}
      <div>
        <div
          style={{
            background: "linear-gradient(135deg, #ffffff 0%, #f8faff 100%)",
            borderRadius: "16px",
            border: "1px solid #ede9fe",
            boxShadow: "0 4px 24px rgba(108,99,255,0.07)",
            padding: "16px 20px",
            marginBottom: "24px",
            position: "relative",
          }}
        >
          <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div>
                <h4
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: 700,
                    color: "#000",
                  }}
                >
                  P&L Charts
                </h4>
                <p style={{ margin: 0, fontSize: "12px", color: "#666" }}>
                  Profit & Loss
                </p>
              </div>
              <div
                style={{ width: "1px", height: "36px", background: "#e2e8f0" }}
              />

              <Button
                onClick={handleExport}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "#0f172a",
                  border: "none",
                  borderRadius: "10px",
                  padding: "8px 16px",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#fff",
                  cursor: "pointer",
                  letterSpacing: "0.2px",
                }}
              >
                <MdDownload size={15} />
                Export
              </Button>

              {currentweek?.[0]?.isValid() && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: "10px",
                    padding: "6px 12px",
                  }}
                >
                  <span
                    style={{
                      width: "7px",
                      height: "7px",
                      borderRadius: "50%",
                      background: "#16a34a",
                      display: "inline-block",
                      boxShadow: "0 0 0 2px #bbf7d0",
                    }}
                  />
                  <div>
                    <div
                      style={{
                        fontSize: "10px",
                        color: "#15803d",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.6px",
                      }}
                    >
                      Latest week Upload
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#166534",
                        fontWeight: 600,
                        marginTop: "1px",
                      }}
                    >
                      {currentweek[0].format("DD MMM")} —{" "}
                      {currentweek[1].format("DD MMM YYYY")}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="d-flex align-items-end gap-2 flex-wrap">
              <RangePicker
                value={dateRange}
                placeholder={["Start (Sat)", "End (Fri)"]}
                onChange={values => setDateRange(values)}
                format="DD-MM-YYYY"
                style={{ borderRadius: "8px", height: "38px" }}
                disabledDate={current => {
                  return maxDate
                    ? current && current > maxDate.endOf("day")
                    : false
                }}
              />
              <Select
                placeholder="Month"
                options={months}
                styles={customStyles}
                isMulti
                value={selectedMonths}
                onChange={v => {
                  setSelectedMonths(v)
                  if (!v || v.length === 0) {
                    setSortGrossPound("chronological")
                    setSortGrossPct("chronological")
                    setSortNetPound("chronological")
                    setSortNetPct("chronological")
                  }
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <Select
                placeholder="Year"
                options={years}
                styles={customStyles}
                isMulti
                value={selectedYears}
                onChange={v => {
                  if (v && v.length > 2) return
                  setSelectedYears(v)
                  if (!v || v.length === 0) {
                    setSortGrossPound("chronological")
                    setSortGrossPct("chronological")
                    setSortNetPound("chronological")
                    setSortNetPct("chronological")
                  }
                }}
                isOptionDisabled={() => selectedYears?.length >= 2}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <Button
                onClick={handleFilter}
                className="btn-blue rounded-2 border-transparent"
              >
                <FaFilter size={12} />
              </Button>
            </div>
          </div>
        </div>

        <Row className="align-items-stretch mb-4">
          {/* <Col md={6} className="mb-4">
            <Card className="bg-white position-relative overflow-hidden shadow rounded-5 mb-0 h-100">
              <CardBody>
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "3px",
                    background:
                      "linear-gradient(90deg, #6C63FF, #F97316, #10B981)",
                    borderRadius: "16px 16px 0 0",
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "4px",
                    padding: "0 8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #6C63FF, #F97316)",
                        boxShadow: "0 0 8px #6C63FF88",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "15px",
                        fontWeight: 700,
                        color: "#1e293b",
                      }}
                    >
                      Gross Margin
                    </span>
                  </div>
                </div>
                <div style={{ height: "380px" }}>
                  <Gauge
                    value={netPL}
                    name="Gross Margin"
                    min={ranges?.[3]?.start}
                    max={ranges?.[3]?.max}
                    formatValue={v =>
                      new Intl.NumberFormat("en-GB", {
                        style: "currency",
                        maximumFractionDigits: 0,
                        currency: "GBP",
                      }).format(v)
                    }
                    ranges={[
                      { limit: ranges?.[3]?.min, color: "#ff3b30" },
                      { limit: ranges?.[3]?.mid, color: "#ffcc00" },
                      { limit: ranges?.[3]?.max, color: "#34c759" },
                    ]}
                    formatTickLabel={value => {
                      const sign = value < 0 ? "-£ " : "£ "
                      const absValue = Math.abs(value)
                      return absValue >= 1000
                        ? `${sign}${Math.round(absValue / 1000)}K`
                        : `${sign}${absValue}`
                    }}
                    newTickLabel={true}
                    type={PLData?.plData?.length > 1 ? "Average" : "Sum"}
                    titleVisible={false}
                  />
                </div>
              </CardBody>
            </Card>
          </Col> */}
          <Col md="12">
            <PieChart
              labels={customerContribution?.labels}
              series={customerContribution?.series}
            />
          </Col>
        </Row>

        <Row className="align-items-stretch mb-4">
          <Col md="6">
            <AreaLineChart
              title="Gross Margin Contribution (£)"
              allWeeks={chartData.grossMargin.allWeeks}
              dateByWeek={chartData.grossMargin.dateByWeek}
              prevDateByWeek={chartData.grossMargin.prevDateByWeek}
              data2={chartData.grossMargin.data2}
              data={chartData.grossMargin.data}
              pound={true}
              sortOrder={sortGrossPound}
              onSortOrderChange={setSortGrossPound}
            />
          </Col>
          <Col md="6">
            <AreaLineChart
              title="Gross Margin Contribution %"
              allWeeks={chartData.grossMarginPercent.allWeeks}
              dateByWeek={chartData.grossMarginPercent.dateByWeek}
              prevDateByWeek={chartData.grossMarginPercent.prevDateByWeek}
              data2={chartData.grossMarginPercent.data2}
              data={chartData.grossMarginPercent.data}
              pound={false}
              sortOrder={sortGrossPct}
              onSortOrderChange={setSortGrossPct}
            />
          </Col>
        </Row>

        <Row className="align-items-stretch mb-4">
          <Col md="6">
            <AreaLineChart
              title="Net Margin Contribution (£)"
              allWeeks={chartData.netMarginFinal.allWeeks}
              dateByWeek={chartData.netMarginFinal.dateByWeek}
              prevDateByWeek={chartData.netMarginFinal.prevDateByWeek}
              data2={chartData.netMarginFinal.data2}
              data={chartData.netMarginFinal.data}
              pound={true}
              sortOrder={sortNetPound}
              onSortOrderChange={setSortNetPound}
            />
          </Col>
          <Col md="6">
            <AreaLineChart
              title="Net Margin Contribution %"
              allWeeks={chartData.netMarginFinalPercent.allWeeks}
              dateByWeek={chartData.netMarginFinalPercent.dateByWeek}
              prevDateByWeek={chartData.netMarginFinalPercent.prevDateByWeek}
              data2={chartData.netMarginFinalPercent.data2}
              data={chartData.netMarginFinalPercent.data}
              pound={false}
              sortOrder={sortNetPct}
              onSortOrderChange={setSortNetPct}
            />
          </Col>
        </Row>

        {/* <Row className="align-items-stretch mb-4">
          <Col md="6">
            <HorizontalBarChart
              categories={negativeCustomerChart?.categories}
              data={negativeCustomerChart?.data}
              title="Negative Contribution"
              Bcolor={"#EC5800"}
            />
          </Col>
          <Col md="6">
            <HorizontalBarChart
              categories={positiveCustomerChart?.categories}
              data={positiveCustomerChart?.data}
              title="Positive Contribution"
              Bcolor={"#228B22"}
            />
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md="12">
            <CombinedContributionBar
              data={PLData}
              title="Net Contribution by Customer & Product"
              dateRange={dateRange}
              selectedMonths={selectedMonths}
              selectedYears={selectedYears}
            />
          </Col>
        </Row> */}

        <Row className="mt-4 mb-4">
          <Col md="12">
            <CategoryContributionChart data={PLData?.categoryContribution || []} />
          </Col>
        </Row>

        <Row className="mt-4 mb-4">
          <Col md="6">
            <ProductContributionChart data={productContributionGrouped} />
          </Col>
          <Col md="6">
            <CustomerContributionChart data={customerContributionGrouped} />
          </Col>
        </Row>

        <Row className="mt-4 mb-4">
          <Col md="6">
            <GrossMarginProductChart data={grossMarginProductGrouped} />
          </Col>
          <Col md="6">
            <GrossMarginCustomerChart data={grossMarginCustomerGrouped} />
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default PLIndex
